import type { CompanyMembership, CompanyType, HrKyc, VerificationStatus } from "../types/api";

export const COMPANY_KYC_SLOT_LABELS: Record<string, string> = {
  gst_certificate: "GST certificate",
  company_pan_card: "Company PAN card",
  address_proof: "Address proof",
  signatory_id: "Signatory ID",
};

export const COMPANY_KYC_REQUIRED_SLOTS = ["company_pan_card"] as const;
export const COMPANY_KYC_OPTIONAL_SLOTS = ["gst_certificate", "address_proof", "signatory_id"] as const;

export const JOIN_ORG_HUB_COPY = {
  client: {
    to: "/join/company",
    title: "Client company",
    subtitle: "Post jobs & hire",
    description:
      "Create your company, upload company PAN, then post roles. Publishing waits on verification and fee review.",
    features: ["Self-serve company pack", "Job posting wizard", "Admin verifies documents"],
  },
  consultancy: {
    to: "/join/consultancy",
    title: "Consultancy",
    hubTitle: "Consultancies",
    subtitle: "Run your desk",
    description:
      "Create your consultancy, verify company PAN, then browse marketplace jobs and invite recruiters.",
    features: ["Invite recruiters", "Marketplace after verify", "Org KYC pack"],
    youCan: [
      "Invite recruiters and run your desk against marketplace jobs",
      "See client names after the consultancy is verified",
      "Cannot post client jobs — those stay on the hiring-company path",
    ],
    required: [
      "Org pack: legal name, company PAN, and signatory",
      "Segments you hire for",
      "Admin verifies the company before marketplace names unmask",
    ],
  },
} as const;

export const ORG_KYC_COPY = {
  pageTitleClient: "Company KYC",
  pageTitleConsultancy: "Consultancy KYC",
  subtitle:
    "Upload company PAN and signatory details. GSTIN is optional if the company is not GST-registered. Marketplace names stay redacted until an admin verifies.",
  submittedTitle: "Documents submitted — waiting for review",
  submittedBody: "An admin will review the pack. You can keep using the portal while you wait.",
  rejectedTitle: "Company documents rejected",
  rejectedFallback: "Update the files and resubmit.",
  verifiedTitle: "Company verified",
  verifiedBody: "This organisation can use unmasked marketplace data and publish jobs (HR KYC still applies).",
  unverifiedBannerTitle: "Company not verified",
  unverifiedClientBody:
    "Complete company KYC. An admin must verify documents before you can publish jobs.",
  unverifiedConsultancyBody:
    "Complete company KYC. Marketplace job names stay redacted until an admin verifies this consultancy.",
  restrictedMemberClientBody:
    "A company admin must complete company KYC before jobs can be published.",
  restrictedMemberConsultancyBody:
    "A company admin must complete company KYC before marketplace job names are unmasked.",
  verifyWithoutDocs: "Verify without docs",
  verifyWithoutDocsHint: "Ops shortcut for companies created by admin. Still records a review stamp.",
  reviewPack: "Review KYC",
  gstinLabel: "GSTIN",
  gstinHint: "Optional. Skip if the company is not GST-registered.",
  optionalSuffix: "(optional)",
  detailsDescription: "Legal name is required.",
  verifyHighlight: "Admin verifies company PAN and signatory before unmask",
  companyPanLabel: "Company PAN",
  companyPanOnFile: "Company PAN is already on file.",
  cinLabel: "CIN / registration no.",
  signatoryNameLabel: "Authorized signatory name",
  signatoryPanLabel: "Authorized signatory PAN",
  signatoryPanOnFile: "Signatory PAN is already on file.",
  billingLine1Label: "Billing address line 1",
  billingCityLabel: "Billing city",
  billingPincodeLabel: "PIN code",
  submitCta: "Submit for review",
};

export const HR_KYC_COPY = {
  pageTitle: "Your identity",
  designationLabel: "Designation",
  personalPanLabel: "Personal PAN (optional)",
  submitCta: "Submit for review",
  bannerTitle: "Complete your HR KYC",
  bannerBody: "You can draft jobs now. Publishing needs your KYC (or an admin bypass) and a verified company.",
  bypassTitle: "KYC bypassed",
  bypassOff: "KYC bypass off",
  bypassBody: "An admin allowed publishing without your personal KYC. Company verification and job fee review still apply.",
  bypassToggle: "Bypass KYC",
  bypassHint: "Lets this person publish without personal KYC. Does not skip company verification or job fee approval.",
  cannotPublishKyc: "Complete HR KYC or ask an admin to bypass before publishing",
  cannotPublishCompany: "Your company must be verified before you can publish jobs",
  cannotPublishContract: "Admin must create an active rate card before publishing",
  rejectDefaultNotes: "Update designation and resubmit",
  personalPanOnFile: "PAN is already on file.",
};

export function orgIsVerified(status?: VerificationStatus | string | null): boolean {
  return status === "verified";
}

export function orgMarketplaceOpen(status?: VerificationStatus | string | null): boolean {
  return status === "verified";
}

export function isCompanyAdmin(membership?: CompanyMembership | null): boolean {
  return membership?.company_role === "company_admin";
}

export function hrCanPublish(membership?: CompanyMembership | HrKyc | null): boolean {
  if (!membership) return false;
  if ("can_publish" in membership && typeof membership.can_publish === "boolean") {
    return membership.can_publish;
  }
  const companyOk = "verification_status" in membership ? membership.verification_status === "verified" : false;
  const personOk = membership.kyc_status === "verified" || Boolean(membership.kyc_bypass);
  return companyOk && personOk;
}

export function hrNeedsIdentityKyc(membership?: CompanyMembership | HrKyc | null): boolean {
  if (!membership) return false;
  if (membership.kyc_bypass) return false;
  return membership.kyc_status === "pending" || membership.kyc_status === "rejected";
}

export function orgKycNavLabel(status?: VerificationStatus | string | null): string {
  if (status === "verified") return "Company KYC";
  if (status === "submitted") return "Company KYC pending";
  if (status === "rejected") return "Fix company KYC";
  return "Complete company KYC";
}

export function joinLandingPath(companyType: CompanyType): string {
  return companyType === "consultancy" ? "/consultancy/kyc" : "/hr/kyc";
}
